import React, { useState, useEffect, useRef } from "react";
import { Fan, Lightbulb, Bell, Camera, Wind, Monitor, Eye, EyeOff } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";

const gasColor = (q) => (q === "DANGER" ? "var(--danger)" : q === "POOR" ? "var(--warning)" : "var(--accent)");

function Btn({ active, onClick, children, danger, disabled, testid, className = "" }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`px-4 py-2.5 rounded-md font-mono text-sm font-medium border transition-colors duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed ${
        active
          ? "bg-[var(--accent)] text-black border-[var(--accent)]"
          : danger
          ? "border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger)]/10"
          : "bg-[var(--surface2)] border-[var(--bd)] text-[var(--t1)] hover:border-[var(--bd-strong)]"
      } ${className}`}
    >
      {children}
    </button>
  );
}

function Group({ icon: Icon, title, badge, children }) {
  return (
    <Panel className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[var(--accent)]" />
        <Overline>{title}</Overline>
        {badge && <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-full bg-[var(--accent-soft)] text-[var(--accent)]">{badge}</span>}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </Panel>
  );
}

export default function Controls() {
  const { sendCamCommand, sendPzemCommand, telemetry, mqttConnected } = useApp();

  const [pzemFan,      setPzemFan]      = useState("auto");
  const [pzemLcdOn,    setPzemLcdOn]    = useState(true);
  const [pzemPirOn,    setPzemPirOn]    = useState(true);
  const [pzemFanGas,   setPzemFanGas]   = useState(true);
  const [pzemBuzGas,   setPzemBuzGas]   = useState(true);
  const [pzemBuzPir,   setPzemBuzPir]   = useState(true);
  const [gasThresh,    setGasThresh]    = useState("600");
  const [pzemLightPct, setPzemLightPct] = useState(0);
  // Track when a manual light command was sent so telemetry can't immediately revert the UI
  const lightCmdAt = useRef(0);
  const LIGHT_LOCK_MS = 10000; // ignore telemetry light_pct for 10s after a manual set

  useEffect(() => {
    if (!telemetry) return;
    if (telemetry.pzem_fan_auto  != null) setPzemFan(telemetry.pzem_fan_auto ? "auto" : telemetry.pzem_fan_pct === 0 ? "off" : String(telemetry.pzem_fan_pct));
    if (telemetry.pzem_lcd_on    != null) setPzemLcdOn(!!telemetry.pzem_lcd_on);
    if (telemetry.pzem_pir_on    != null) setPzemPirOn(!!telemetry.pzem_pir_on);
    if (telemetry.pzem_fan_on_gas     != null) setPzemFanGas(!!telemetry.pzem_fan_on_gas);
    if (telemetry.pzem_buzzer_on_gas  != null) setPzemBuzGas(!!telemetry.pzem_buzzer_on_gas);
    if (telemetry.pzem_buzzer_on_pir  != null) setPzemBuzPir(!!telemetry.pzem_buzzer_on_pir);
    if (telemetry.pzem_gas_thresh != null) setGasThresh(String(Math.round(telemetry.pzem_gas_thresh)));
    // Only sync light from telemetry when not in the post-command lock window
    if (telemetry.light_pct != null && Date.now() - lightCmdAt.current > LIGHT_LOCK_MS) {
      setPzemLightPct(telemetry.light_pct);
    }
  }, [telemetry]);

  return (
    <div className="space-y-6">
      <div>
        <Overline>Device Commands</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Remote Control</h2>
        <p className="text-sm text-[var(--t2)] mt-1">
          Commands publish to{" "}
          <span className="font-mono text-[var(--accent)]">campus/energy/pzem_cmd</span> via HiveMQ.{" "}
          {!mqttConnected && <span className="text-[var(--danger)] font-mono">MQTT OFFLINE</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Fan */}
        <Group icon={Fan} title="Fan" badge={pzemFan === "auto" ? "AUTO" : pzemFan === "off" ? "OFF" : pzemFan + "%"}>
          {["auto","off","30","60","100"].map((v) => (
            <Btn key={v} active={pzemFan === v} testid={`pzem-fan-${v}`}
              onClick={() => { setPzemFan(v); sendPzemCommand("fan", v, `Fan ${v}`); }}>
              {v === "auto" ? "AUTO" : v === "off" ? "OFF" : v + "%"}
            </Btn>
          ))}
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">GPIO 25 · 12V · IRLZ44N · auto-tracks 3-phase load</p>
        </Group>

        {/* Light Hub */}
        <Group icon={Lightbulb} title="Light Hub" badge={`${pzemLightPct}%`}>
          {[["OFF","0"],["25%","25"],["50%","50"],["100%","100"]].map(([lbl,v]) => (
            <Btn key={v} active={String(pzemLightPct) === v} testid={`pzem-light-${v}`}
              onClick={() => {
                lightCmdAt.current = Date.now();
                setPzemLightPct(Number(v));
                sendPzemCommand("light", v === "0" ? "off" : v, `Light ${lbl}`);
              }}>
              {lbl}
            </Btn>
          ))}
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">GPIO 32 · PWM · IRLZ44N · auto-dims on PIR unless firmware re-flashed</p>
        </Group>

        {/* LCD */}
        <Group icon={Monitor} title="LCD (20×4 I²C)" badge={pzemLcdOn ? "ON" : "OFF"}>
          <Btn active={pzemLcdOn} testid="pzem-lcd-on"
            onClick={() => { setPzemLcdOn(true);  sendPzemCommand("lcd", "on",  "LCD ON"); }}>
            ON
          </Btn>
          <Btn active={!pzemLcdOn} testid="pzem-lcd-off"
            onClick={() => { setPzemLcdOn(false); sendPzemCommand("lcd", "off", "LCD OFF"); }}>
            OFF
          </Btn>
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">GPIO 21 SDA · GPIO 22 SCL · addr 0x27</p>
        </Group>

        {/* PIR */}
        <Group icon={Eye} title="PIR Motion (HC-SR501)" badge={pzemPirOn ? "ACTIVE" : "DISABLED"}>
          <Btn active={pzemPirOn} testid="pzem-pir-on"
            onClick={() => { setPzemPirOn(true);  sendPzemCommand("pir", "on",  "PIR ON"); }}>
            <Eye size={13} className="inline mr-1" /> ENABLE
          </Btn>
          <Btn active={!pzemPirOn} testid="pzem-pir-off"
            onClick={() => { setPzemPirOn(false); sendPzemCommand("pir", "off", "PIR OFF"); }}>
            <EyeOff size={13} className="inline mr-1" /> DISABLE
          </Btn>
          <div className="w-full mt-2 pt-2 border-t border-[var(--bd)] flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[var(--t2)] font-mono">Buzzer on motion:</span>
            <Btn active={pzemBuzPir} testid="pzem-buz-pir-on"
              onClick={() => { setPzemBuzPir(true);  sendPzemCommand("buzzer_pir", "on",  "Buzzer PIR ON"); }}>ON</Btn>
            <Btn active={!pzemBuzPir} testid="pzem-buz-pir-off"
              onClick={() => { setPzemBuzPir(false); sendPzemCommand("buzzer_pir", "off", "Buzzer PIR OFF"); }}>MUTE</Btn>
          </div>
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">GPIO 4 · light hub auto-fades on motion</p>
        </Group>

        {/* Gas */}
        <Group icon={Wind} title="Gas Sensor (MQ-135)" badge={telemetry?.pzem_gas_quality ?? "—"}>
          {telemetry?.pzem_gas_quality && (
            <div className="w-full mb-2 flex items-center gap-3">
              <span className="text-2xl font-mono font-bold" style={{ color: gasColor(telemetry.pzem_gas_quality) }}>
                {telemetry.pzem_gas_quality}
              </span>
              {telemetry.pzem_gas_ppm != null && (
                <span className="text-sm font-mono text-[var(--t2)]">{Math.round(telemetry.pzem_gas_ppm)} ppm</span>
              )}
            </div>
          )}
          <div className="w-full flex items-center gap-2 mb-2">
            <span className="text-xs font-mono text-[var(--t2)] whitespace-nowrap">Alert threshold:</span>
            <input type="number" min="50" max="1000" value={gasThresh}
              onChange={(e) => setGasThresh(e.target.value)}
              className="w-20 px-2 py-1 rounded border border-[var(--bd)] bg-[var(--surface2)] text-[var(--t1)] font-mono text-sm text-center" />
            <span className="text-xs text-[var(--t2)]">ppm</span>
            <Btn testid="pzem-gas-thresh-apply"
              onClick={() => {
                const v = parseFloat(gasThresh);
                if (!isNaN(v) && v >= 50 && v <= 1000)
                  sendPzemCommand("gas_thresh", String(Math.round(v)), `Gas threshold ${Math.round(v)} ppm`);
              }}>Apply</Btn>
          </div>
          <div className="w-full flex flex-wrap gap-2">
            <span className="text-xs font-mono text-[var(--t2)] self-center">Fan on alert:</span>
            <Btn active={pzemFanGas} testid="pzem-fan-gas-on"
              onClick={() => { setPzemFanGas(true);  sendPzemCommand("fan_gas", "on",  "Fan-on-Gas ON"); }}>ON</Btn>
            <Btn active={!pzemFanGas} testid="pzem-fan-gas-off"
              onClick={() => { setPzemFanGas(false); sendPzemCommand("fan_gas", "off", "Fan-on-Gas OFF"); }}>OFF</Btn>
            <span className="text-xs font-mono text-[var(--t2)] self-center ml-2">Buzzer:</span>
            <Btn active={pzemBuzGas} testid="pzem-buz-gas-on"
              onClick={() => { setPzemBuzGas(true);  sendPzemCommand("buzzer_gas", "on",  "Buzzer-on-Gas ON"); }}>ON</Btn>
            <Btn active={!pzemBuzGas} testid="pzem-buz-gas-off"
              onClick={() => { setPzemBuzGas(false); sendPzemCommand("buzzer_gas", "off", "Buzzer-on-Gas OFF"); }}>MUTE</Btn>
          </div>
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">GPIO 34 · ADC · 10kΩ+22kΩ divider</p>
        </Group>

        {/* Buzzer */}
        <Group icon={Bell} title="Buzzer" badge="GPIO 18">
          <Btn testid="pzem-buz-beep" onClick={() => sendPzemCommand("buzzer", "beep", "Buzz test")}>BEEP</Btn>
          <Btn testid="pzem-buz-alert" danger onClick={() => sendPzemCommand("buzzer", "alert", "Buzz alert")}>
            <Bell size={13} className="inline mr-1" /> ALERT
          </Btn>
          <Btn testid="pzem-buz-stop" onClick={() => sendPzemCommand("buzzer", "stop", "Buzz stop")}>SILENCE</Btn>
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">TIP120 driver · 5V piezo buzzer</p>
        </Group>

        {/* Camera Snapshot */}
        <Group icon={Camera} title="Camera Snapshot" badge="TTGO">
          <Btn testid="cam-snapshot" onClick={() => sendCamCommand("snapshot")}>
            <Camera size={14} className="inline mr-1" /> Trigger Snapshot
          </Btn>
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">
            Publishes to <span className="text-[var(--accent)]">campus/camera/cmd</span>
          </p>
        </Group>

      </div>
    </div>
  );
}
